import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getHsnCodesApi,
  createHsnCodeApi,
  updateHsnCodeApi,
  deleteHsnCodeApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

export default function HsnCodes() {
  const [hsnCodes, setHsnCodes] = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [form] = Form.useForm();

  const fetchHsnCodes = async () => {
    try {
      const res = await getHsnCodesApi();
      setHsnCodes((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load HSN codes");
    }
  };

  useEffect(() => { fetchHsnCodes(); }, []);

  const displayed = hsnCodes.filter(
    (h) =>
      !search ||
      h.code?.toLowerCase().includes(search.toLowerCase()) ||
      h.description?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      code:        record.code,
      description: record.description,
      gstRate:     record.gstRate,
      isActive:    record.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        code:        values.code,
        description: values.description || null,
        gstRate:     Number(values.gstRate),
        active:      values.isActive,
      };
      if (editing) {
        await updateHsnCodeApi(editing.id, payload);
        message.success("HSN code updated");
      } else {
        await createHsnCodeApi(payload);
        message.success("HSN code added");
      }
      setOpen(false);
      fetchHsnCodes();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save HSN code");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateHsnCodeApi(record.id, {
        code:        record.code,
        description: record.description,
        gstRate:     record.gstRate,
        active:      !record.active,
      });
      message.success(record.active ? "HSN code deactivated" : "HSN code activated");
      fetchHsnCodes();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete this HSN code?",
      content:
        record.productCount > 0
          ? `${record.productCount} product(s) currently use this code — deleting is blocked. Deactivate it instead if you no longer want it selectable.`
          : "This cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true, disabled: record.productCount > 0 },
      onOk: async () => {
        try {
          await deleteHsnCodeApi(record.id);
          message.success("HSN code deleted");
          fetchHsnCodes();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to delete HSN code");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "HSN Code",
      dataIndex: "code",
      render: (v) => <span style={{ fontWeight: 600, color: "#111827" }}>{v}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (v) => v || <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "GST Rate",
      dataIndex: "gstRate",
      width: 110,
      align: "center",
      render: (v) => <Tag color="purple">{v}%</Tag>,
    },
    {
      title: "Products",
      dataIndex: "productCount",
      width: 100,
      align: "center",
      render: (v) => <span className="badge-pill badge-blue">{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      align: "center",
      render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="default">Off</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">HSN / GST Rates</div>
          <div className="page-sub">
            {hsnCodes.length} HSN codes — each product's GST rate is derived from the HSN code assigned to it
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add HSN Code
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by code or description…"
          allowClear
          style={{ width: 320, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AppTable columns={columns} data={displayed} />

      <Modal
        title={editing ? `Edit — ${editing.code}` : "Add HSN Code"}
        open={open}
        okText={editing ? "Save Changes" : "Add HSN Code"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="code"
            label="HSN Code"
            rules={[{ required: true, message: "HSN code is required" }]}
            placeholder="e.g. 6109"
          />

          <AppInput
            name="description"
            label="Description (optional)"
            placeholder="e.g. T-shirts, knitted or crocheted"
          />

          <Form.Item
            name="gstRate"
            label="GST Rate (%)"
            rules={[{ required: true, message: "GST rate is required" }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} placeholder="e.g. 5, 12, 18" />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
